import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import gsap from 'gsap';

/**
 * Speed-climbing finish buzzer — modeled on a hotel service bell (gold
 * dome on a flat plate with a tiny push-knob on top). Smaller than the
 * earlier hanging-bell sketch. On `triggerSlam()` a stylized hand slams
 * down onto the dome, the bell squashes + shakes, ripple rings burst
 * outward, "DING!" text floats up, and a short beep plays via Web Audio.
 */
export class FinishBell extends Container {
  private body: Graphics;
  private rings: Graphics;
  private dingText: Text;
  private hand: Text;
  private struck = false;

  constructor() {
    super();
    this.body = new Graphics();
    this.rings = new Graphics();
    this.dingText = new Text({
      text: 'DING!',
      style: new TextStyle({
        fontFamily: 'monospace',
        fontSize: 16,
        fontWeight: '900',
        fill: 0xf4c724,
        stroke: { color: 0x000000, width: 3 },
        align: 'center',
      }),
    });
    this.dingText.anchor.set(0.5);
    this.dingText.alpha = 0;
    // Hand — emoji as a quick stand-in for a sprite. Anchored at the palm.
    this.hand = new Text({
      text: '✋',
      style: new TextStyle({
        fontFamily: 'serif',
        fontSize: 28,
      }),
    });
    this.hand.anchor.set(0.5, 0.85);
    this.hand.alpha = 0;
    this.addChild(this.rings);
    this.addChild(this.body);
    this.addChild(this.dingText);
    this.addChild(this.hand);
    this.drawIdle();
  }

  private drawIdle(): void {
    const g = this.body;
    g.clear();
    // Service bell sized ~30×24 (down from the 44×40 hanging bell).
    // Base plate — gold disc the dome sits on.
    g.ellipse(0, 9, 18, 4).fill({ color: 0x000000, alpha: 0.3 }); // soft shadow on the ground
    g.roundRect(-16, 4, 32, 6, 2).fill({ color: 0xb8941d }); // plate body
    g.roundRect(-16, 4, 32, 1.5, 1).fill({ color: 0xffe082, alpha: 0.9 }); // plate top highlight
    // Dome — gold half-circle, ~24 wide × 14 tall.
    g.moveTo(-12, 4)
      .arcTo(-12, -10, 0, -10, 12)
      .arcTo(12, -10, 12, 4, 12)
      .lineTo(12, 4)
      .closePath()
      .fill({ color: 0xf4c724 });
    // Sun-lit edge on the dome.
    g.moveTo(-9, 2)
      .quadraticCurveTo(-11, -6, -4, -8)
      .stroke({ width: 2, color: 0xffe082, alpha: 0.85 });
    // Push-knob on top — classic ring-for-service button.
    g.circle(0, -11, 2.5).fill({ color: 0xb8941d });
    g.circle(0, -11.5, 1.5).fill({ color: 0xffe082 });
  }

  /** Play the finish strike. Idempotent — second call is a no-op.
   *  Sequence: hand drops → impact → bell squash + shake + rings + sound
   *  → hand recoils up + fades. */
  triggerSlam(opts?: { sound?: boolean }): void {
    if (this.struck) return;
    this.struck = true;

    // Hand starts above the bell, fully visible, then drops fast onto the dome.
    const handStartY = -54;
    const handImpactY = -16;
    this.hand.x = 0;
    this.hand.y = handStartY;
    this.hand.alpha = 0;
    this.hand.rotation = -0.25;

    const slamTl = gsap.timeline();
    // Drop in (fade as it falls).
    slamTl.to(this.hand, { alpha: 1, duration: 0.08 });
    slamTl.to(this.hand, {
      y: handImpactY,
      rotation: 0.05,
      duration: 0.18,
      ease: 'power3.in',
    });
    // Impact frame — small overshoot into the dome.
    slamTl.to(this.hand, {
      y: handImpactY + 4,
      duration: 0.05,
      ease: 'power2.out',
      onStart: () => this.runBellImpact(opts?.sound !== false),
    });
    // Recoil + fade out.
    slamTl.to(this.hand, {
      y: -38,
      rotation: -0.15,
      alpha: 0,
      duration: 0.4,
      ease: 'power2.out',
    });
  }

  /** Squash + shake + rings + DING! — split out so the slam timeline can
   * fire it at the exact impact frame. */
  private runBellImpact(sound: boolean): void {
    // Pulse the bell body — quick squash + recoil.
    gsap.to(this.scale, {
      x: 1.35,
      y: 0.75,
      duration: 0.08,
      ease: 'power3.out',
      onComplete: () => {
        gsap.to(this.scale, {
          x: 1,
          y: 1,
          duration: 0.5,
          ease: 'elastic.out(1.4, 0.4)',
        });
      },
    });

    // Shake the bell side-to-side for ~0.4s.
    const shakeTl = gsap.timeline();
    for (let i = 0; i < 5; i++) {
      shakeTl.to(this.body, { rotation: i % 2 === 0 ? 0.18 : -0.18, duration: 0.06 });
    }
    shakeTl.to(this.body, { rotation: 0, duration: 0.15, ease: 'sine.out' });

    // Ripple rings burst outward — three concentric expanding circles.
    for (let r = 0; r < 3; r++) {
      const ring = new Graphics();
      this.rings.addChild(ring);
      const startR = 14;
      const endR = 64 + r * 12;
      const start = { r: startR, a: 0.9 };
      gsap.to(start, {
        r: endR,
        a: 0,
        duration: 0.8 + r * 0.15,
        ease: 'power2.out',
        delay: r * 0.1,
        onUpdate: () => {
          ring.clear()
            .circle(0, 0, start.r)
            .stroke({ width: 3, color: 0xf4c724, alpha: start.a });
        },
        onComplete: () => {
          ring.destroy();
        },
      });
    }

    // DING! text float-up — appears just above the bell so the impact reads.
    this.dingText.y = -16;
    this.dingText.alpha = 1;
    this.dingText.scale.set(0.6);
    gsap.to(this.dingText.scale, { x: 1.2, y: 1.2, duration: 0.25, ease: 'back.out(2)' });
    gsap.to(this.dingText, {
      y: -48,
      alpha: 0,
      duration: 0.9,
      delay: 0.25,
      ease: 'power2.out',
    });

    if (sound) playDingSound();
  }
}

/** Synthesize a short "ding" via the Web Audio API — no asset needed. */
function playDingSound(): void {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const t0 = ctx.currentTime;
    // Bell timbre = bright sine fundamental + a couple of inharmonic partials,
    // each with an exponential decay.
    [
      { f: 1320, gain: 0.32, decay: 1.1 },
      { f: 2640, gain: 0.18, decay: 0.55 },
      { f: 3960, gain: 0.08, decay: 0.4 },
    ].forEach(({ f, gain, decay }) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      g.gain.setValueAtTime(gain, t0);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + decay);
      osc.connect(g).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + decay);
    });
    // Auto-close the context so we don't leak audio sessions.
    window.setTimeout(() => { void ctx.close(); }, 1500);
  } catch { /* silent */ }
}
